import {
  BMComponentsType,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcPaymentRefund16 from "@traveloka/icon-kit-web/react/IcPaymentRefund16";
import { useMemo } from "react";
import { Image, StyleSheet } from "react-native";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { FlightRefundInfoItemComponentProps } from "./types";

type FlightRefundInfoComponentProps = {
  refundInfos: Array<FlightRefundInfoItemComponentProps>;
};

type FlightRefundInfoComponent = React.FC<FlightRefundInfoComponentProps> & {
  Item: typeof FlightRefundInfoItem;
};

/**
 * FlightRefundInfo component to show flight refund information list
 * Mapped all data into flight refund information item component
 */
const FlightRefundInfos: FlightRefundInfoComponent = ({ refundInfos }) => {
  return (
    <BMView spacing="md">
      {refundInfos.map((info, index) => (
        <FlightRefundInfoItem key={index} {...info} />
      ))}
    </BMView>
  );
};

/**
 * Expose FlightRefundInfoItem as a static property of FlightRefundInfo
 * so it can be accessed as FlightRefundInfo.Item
 */
FlightRefundInfos.Item = FlightRefundInfoItem;

/**
 * FlightRefundInfoItem component to show flight refund information item
 * including departure, arrival, duration, airlines, seat class, and stop info
 */
function FlightRefundInfoItem({
  status,
  airlineName,
  airlineLogos,
  seatClass,
  arrivalCity,
  departureCity,
  policies,
}: FlightRefundInfoItemComponentProps) {
  const { BMComponents } = useBMTheme();
  const { CorporateFlightRefundRescheduleEnum: cr } =
    useMobileContentResource();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);
  const isRefund = useMemo(() => status === "REFUNDABLE", [status]);
  return (
    <BMView style={styles.container}>
      <BMView
        spacing="sm"
        style={[styles.content, policies.length ? styles.withDivider : null]}
      >
        <BMView>
          <BMView row spacing="md" align="center">
            <BMText variant="uiLarge">{airlineName}</BMText>
            <BMView row spacing="sm" align="center">
              {airlineLogos.map((item) => (
                <Image
                  key={item.src}
                  source={{
                    uri: item.src,
                  }}
                  style={styles.airlinesImg}
                  resizeMode="contain"
                />
              ))}
            </BMView>
          </BMView>
          <BMText variant="uiLargeLi" ink="BMStyleTextInkSecondary">
            {departureCity} → {arrivalCity} • {seatClass}
          </BMText>
        </BMView>
        <BMView row align="center" spacing="sm">
          <IcPaymentRefund16
            width={16}
            height={16}
            color={
              isRefund
                ? BMComponents.BMStyleIconInkHighlightGreen
                : BMComponents.BMStyleIconInkSecondary
            }
          />
          <BMText
            variant="uiLarge"
            ink={
              isRefund ? "BMStyleTextInkHighlight" : "BMStyleTextInkSecondary"
            }
          >
            {cr[status]}
          </BMText>
        </BMView>
      </BMView>
      {policies.length ? (
        <BMView style={styles.content} spacing="xs">
          {policies.map((policy, index) => (
            <BMView
              row
              spacing="sm"
              key={`${airlineName}_${departureCity}_${arrivalCity}-policy-${index}`}
              style={styles.li}
            >
              <BMText variant="paragraphSmall" ink="BMStyleTextInkSecondary">
                •
              </BMText>
              <BMText variant="paragraphSmall" ink="BMStyleTextInkSecondary">
                {policy}
              </BMText>
            </BMView>
          ))}
        </BMView>
      ) : null}
    </BMView>
  );
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    container: {
      borderRadius: BMPrimitives.BMRadius.md,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorInventory,
    },
    withDivider: {
      borderBottomWidth: BMPrimitives.BMBorder.width.thick,
      borderBottomColor: BMComponents.BMStyleLineBordercolorSubtle,
    },
    content: {
      padding: BMPrimitives.BMSize.md,
    },
    li: {
      marginLeft: BMPrimitives.BMSize.xs,
    },
    airlinesImg: {
      width: BMPrimitives.BMSize.xl,
      height: BMPrimitives.BMSize.xl,
    },
  });
}

export default FlightRefundInfos;
